import { format, getDay } from "date-fns";
import { DAY_TYPE } from "~/common/constants/form";
import translation from "~/i18n";

/**
 * @param {Date || string} date
 * @param {string} dateFormat
 */
export const dateToString = (date, dateFormat = "yyyyMMddHHmm") => {
	try {
		if (typeof date === "string") {
			return format(new Date(date), dateFormat);
		} else {
			return format(date, dateFormat);
		}
	} catch {
		return "";
	}
};

/**
 * @param {number} phoneNumber phone number
 * @example phoneNumberFormatter("01011112222")
 */
export const phoneNumberFormatter = (phoneNumber) => {
	try {
		return phoneNumber.toString().replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
	} catch {
		return phoneNumber;
	}
};

/**
 * @param {number} faxNumber fax number
 * @example faxNumberFormatter("01011112222")
 */
export const faxNumberFormatter = (faxNumber) => {
	try {
		return faxNumber.toString().replace(/(\d{2})(\d{4})(\d{4})/, "$1-$2-$3");
	} catch {
		return faxNumber;
	}
};

/**
 * @param {number} companyNumber company number
 * @example companyNumberFormatter(1231212345)
 */
export const companyNumberFormatter = (companyNumber) => {
	try {
		return companyNumber.toString().replace(/(\d{3})(\d{2})(\d{5})/, "$1-$2-$3");
	} catch {
		return companyNumber;
	}
};

/**
 * @param {number} number number
 * @example thousandFormatter(1000000)
 */
export const thousandFormatter = (number) => {
	try {
		return Number(number || 0).toLocaleString();
	} catch {
		return number;
	}
};

/**
 * @param {number} ssn social security number
 * @example ssnFormatter(0000001111111)
 */
export const ssnFormatter = (ssn) => {
	try {
		return ssn.toString().replace(/(\d{6})(\d{7})/, "$1-$2");
	} catch {
		return ssn.toString();
	}
};

/**
 * @param {number} "hhmmss" time string
 * @example timeFormatter(121212,":")
 */
export const timeFormatter = (time, separator) => {
	try {
		return time.toString().replace(/(\d{2})(\d{2})(\d{2})/, `$1${separator}$2${separator}$3`);
	} catch {
		return time.toString();
	}
};

/**
 * @param {number} "hhmmss" time string
 * @example dateTimeFormatter(20210101121212)
 */
export const dateTimeFormatter = (dateTime) => {
	try {
		return dateTime
			.toString()
			.replace(/(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, `$1.$2.$3 $4:$5:$6`);
	} catch {
		return dateTime.toString();
	}
};

/**
 * @param {number} number zeroLeadingFormatter
 * @example zeroLeadingFormatter(1)
 */
export const zeroLeadingFormatter = (number) => {
	try {
		return number.toString().padStart(2, "0");
	} catch {
		return number.toString();
	}
};

/**
 * @param {string|Date} date string(yyyyMMdd, yyyyMM) or date object
 * @param {string} [sep] string separator(option)
 * @example dateFormatter(new Date(), "-")
 */
export const dateFormatter = (date, separator) => {
	try {
		if (typeof date === "string" && date.length === 8) {
			return date
				.toString()
				.replace(
					/(\d{4})(\d{2})(\d{2})/,
					`$1${separator ? separator : "."}$2${separator ? separator : "."}$3`
				);
		} else if (typeof date === "string" && date.length === 6) {
			return date.toString().replace(/(\d{4})(\d{2})/, `$1${separator ? separator : "."}$2`);
		} else {
			const y = date.getFullYear();
			const m = zeroLeadingFormatter(date.getMonth() + 1);
			const d = zeroLeadingFormatter(date.getDate());
			return `${y}${separator ? separator : ""}${m}${separator ? separator : "."}${d}`;
		}
	} catch (e) {
		if (date === null || date === undefined) {
			return "";
		} else {
			return date.toString();
		}
	}
};

/**
 * @param {string} link
 * @example extLinkFormatter("instagram.com")
 */
export const extLinkFormatter = (link) => {
	if (link === undefined || link === null || link.length === 0) {
		return undefined;
	} else {
		return link.startsWith("http") ? link : `https://${link}`;
	}
};

/**
 * @param {string} bytes
 * @example bytesToReadableUnit("269651")
 */
export const bytesToReadableUnit = (bytes) => {
	try {
		var sizes = ["Bytes", "KB", "MB", "GB", "TB"];
		if (bytes == 0) {
			return "0 Byte";
		}
		var i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
		if (i > 4) {
			return Math.round(bytes / Math.pow(1024, i), 2);
		}
		return Math.round(bytes / Math.pow(1024, i), 2) + " " + sizes[i];
	} catch {
		return bytes;
	}
};

export function convertToBillion(number) {
	const billionUnit = 1000000000; // 1 billion = 1,000,000,000
	const billionUnitValue = Math.floor(number / billionUnit); // Convert the integer part to units of billion
	const decimalPart = (number % billionUnit) / billionUnit; // Calculate the decimal part

	// Handle the decimal part only if it exists
	if (decimalPart > 0) {
		const decimalPartStr = decimalPart.toFixed(2).substring(2); // Display up to 9 decimal places and extract only the decimal part
		return `${billionUnitValue}.${decimalPartStr} ${translation.t(
			"pages.investment.InvestListPage.InvestListTable.unit01"
		)}`;
	} else {
		return `${billionUnitValue} ${translation.t(
			"pages.investment.InvestListPage.InvestListTable.unit01"
		)}`;
	}
}

/**
 * @param {Date} date
 * @param {String} format
 * @example DateWithDayName(Mon May 16 2022 00:00:00 GMT+0900) = "5월 16일 (월)"
 */
export const DateWithDayName = (date, format = "M월 dd일 ") => {
	const newDate = dateToString(date, format);
	const dayName = DAY_TYPE[getDay(new Date(date))];
	return newDate + "(" + dayName + ")";
};

/**
 * @param {string} cardNumber
 * @example CardNumberFormatter("4100********4100") = "4100-****-****-4100"
 */
export const CardNumberFormatter = (cardNumber) => {
	return cardNumber.match(/[0-9*]{1,4}/g).join("-");
};
